import { useQuery } from "@tanstack/react-query";

import {
  operatingModeApi,
  operatingModeQueryKeys,
  type OperatingModule,
} from "../api/operatingMode";
import { usePlanScreens } from "../screens/usePlanScreens";

/**
 * Phòng khám này có dùng `module` bên trong Addy không: chế độ vận hành phải gồm nó (ADR-0048,
 * #18965) VÀ gói của phòng khám phải mở vùng đó (ADR-0053).
 *
 * <p>Đọc từ máy chủ chứ không suy từ chế độ ở phía trình duyệt: đúng danh sách đó là thứ sinh ra
 * 409 trên endpoint, và hai bản sao của một luật thì sẽ lệch nhau. Cùng một query key với
 * `Sidebar`, nên React Query dùng lại cache — không thêm một lượt gọi nào.
 *
 * <p><b>Hai câu trả lời, vì có hai câu hỏi khác nhau — đừng dùng nhầm.</b>
 *
 * <ul>
 *   <li>{@code operates} lạc quan: chưa biết thì coi như CÓ. Dùng để <i>vẽ</i>. Ẩn khi chưa biết
 *       sẽ làm màn hình của một phòng khám FULL_HIS — tức gần như mọi phòng khám — nháy một
 *       khoảng trống mỗi lần tải lại trang.
 *   <li>{@code loaded} cho mọi chỗ mà quyết định kéo theo một lượt <i>gọi</i> API — kể cả khi nó
 *       trông như một quyết định vẽ. Một thẻ chỉ tồn tại để hiện kết quả của một query, hay một
 *       widget mang query riêng, thì vẽ nó CHÍNH LÀ gọi API: dùng {@code loaded && operates}.
 *       Chỉ dựa vào {@code operates} là mỗi lần mở màn hình vẫn bắn đúng một request bị 409 cho
 *       mọi phòng khám PATIENT_CHANNEL — không hỏng gì, nhưng đổ một dòng lỗi vào log của họ đều
 *       đặn, để rồi log-watch mở ticket cho một thứ đúng theo thiết kế.
 * </ul>
 *
 * <p>Quy tắc gọn: <b>vẽ không tốn gì thì lạc quan; vẽ mà tốn một request thì chờ.</b>
 */
export function useOperatesModule(module: OperatingModule): {
  operates: boolean;
  loaded: boolean;
} {
  const modules = useOperatedModules();
  return { operates: modules.operates(module), loaded: modules.loaded };
}

/**
 * Cùng câu hỏi với {@link useOperatesModule}, cho những nơi phải hỏi về NHIỀU module một lúc —
 * các tab của hồ sơ bệnh án, các nhóm quyền của ma trận phân quyền.
 */
export function useOperatedModules(): {
  operates: (module: OperatingModule) => boolean;
  loaded: boolean;
} {
  const q = useQuery({
    queryKey: operatingModeQueryKeys.current(),
    queryFn: () => operatingModeApi.get(),
    staleTime: 5 * 60_000,
  });
  // Gói của phòng khám (ADR-0053) là điều kiện thứ hai: một phòng khám FULL_HIS ở gói không có
  // Báo cáo cũng không dùng vùng báo cáo, và mọi trang có bản rút gọn hỏi đúng chỗ này — nên
  // thêm điều kiện ở đây là cả Tổng quan lẫn Tiếp đón tự rút gọn theo gói.
  const plan = usePlanScreens();
  return {
    operates: (module) =>
      (q.data ? q.data.modules.includes(module) : true) && plan.zoneOpen(module),
    loaded: q.isSuccess && plan.settled,
  };
}
